import type { AssetClassType } from "@/types/shared/AssetClass";
/**
 * components/shared/Brokers/BrokerSelectionForm.tsx
 * Reusable broker selection form component
 * 
 * Handles broker selection business logic including asset class conversions,
 * validation, and broker selection state management. Designed for use in
 * both onboarding and Settings contexts.
 */

// 1. External dependencies
import React from 'react';

// 2. Internal absolute imports
import { BrokerSelector } from '@/components/shared/BrokerSelector';
import { 
  type BrokerSelectionAccountData
} from './brokerSelectionUtils';
import { GENERAL_BROKER_ACCOUNT_TYPES } from '@/types/reference/BrokerCatalog';

/**
 * Asset class to broker asset class mapping for prop firms
 */
const PROP_ASSET_CLASS_TO_BROKER_ASSET_CLASSES: Record<string, AssetClassType[]> = {
  'futures': ['futures'],
  'forex-cfd': ['forex', 'cfd'],
  'stocks': ['stocks'], 
  'crypto': ['crypto'],
  'options': ['options'],
  'other': ['stocks', 'options', 'futures', 'forex', 'cfd', 'crypto', 'bonds'] // Show all asset classes
};

/**
 * Convert general account asset classes to broker asset classes
 */
const convertAssetClassesToBrokerAssetClasses = (assetClasses: AssetClassType[]): AssetClassType[] => {
  const brokerAssetClasses: AssetClassType[] = [];
  
  assetClasses.forEach(assetClass => {
    const brokerAssets = PROP_ASSET_CLASS_TO_BROKER_ASSET_CLASSES[assetClass];
    if (brokerAssets) {
      brokerAssetClasses.push(...brokerAssets);
    }
  });
  
  return [...new Set(brokerAssetClasses)]; // Remove duplicates
};


/**
 * Props for BrokerSelectionForm component
 */
export interface BrokerSelectionFormProps {
  /** Account data for broker selection */
  accountData: BrokerSelectionAccountData;
  /** Broker selection handler */
  onBrokerSelect: (brokerId: string) => void;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Optional custom search placeholder */
  searchPlaceholder?: string;
  /** Whether to show fees on broker cards */
  showFees?: boolean;
  /** Whether to show asset indicators */
  showAssetIndicators?: boolean;
  /** Optional additional CSS classes */
  className?: string;
  /** Whether to show custom broker option */
  showCustomBroker?: boolean;
  /** Existing custom broker data */
  customBroker?: {
    name: string;
    id: string;
  };
}




/**
 * Reusable broker selection form component
 */
export const BrokerSelectionForm: React.FC<BrokerSelectionFormProps> = ({
  accountData,
  onBrokerSelect,
  disabled = false,
  searchPlaceholder,
  showFees = true,
  showAssetIndicators = true,
  className = '',
  showCustomBroker = false,
  customBroker
}) => {
  const {
    accountType,
    selectedAssetClasses,
    propFirmId,
    selectedBrokerId
  } = accountData;

  // Get relevant asset classes for display
  // General accounts: use asset classes directly, Prop accounts: use conversion
  const isGeneralAccount = accountType && (GENERAL_BROKER_ACCOUNT_TYPES as readonly string[]).includes(accountType);
  const relevantAssets = isGeneralAccount
    ? selectedAssetClasses.map(cls => cls as AssetClassType)  // Direct mapping for general accounts
    : convertAssetClassesToBrokerAssetClasses(selectedAssetClasses as AssetClassType[]); // Conversion for prop accounts
  

  
  // Generate search placeholder if not provided
  const placeholder = searchPlaceholder || 
    (accountType === 'paper'
      ? 'Search paper trading and real brokers...'
      : accountType === 'prop'
      ? 'Search brokers for your prop firm...'
      : 'Search brokers for your account type...'
    );

  // Construct filter options with proper handling for all account types
  const filterOptions = accountType === 'paper' ? {
    accountType: 'paper' as const,
    // Don't pass requiredAssetClasses - paper trading supports everything
  } : accountType === 'prop' ? {
    accountType: 'prop' as const,
    requiredAssetClasses: convertAssetClassesToBrokerAssetClasses(selectedAssetClasses as AssetClassType[]), // Conversion for prop accounts
    ...(propFirmId && { propFirmId })
  } : {
    // All general account types (personal, retirement, business, trust, pooled, other)
    ...(accountType && { accountType }),
    requiredAssetClasses: selectedAssetClasses as AssetClassType[]  // Direct mapping for general accounts
  };


  return (
    <div className={className}>
      <BrokerSelector
        selectedBrokerId={selectedBrokerId || ''}
        onBrokerSelect={onBrokerSelect}
        filterOptions={filterOptions}
        relevantAssets={relevantAssets}
        showFees={showFees}
        showAssetIndicators={showAssetIndicators}
        searchPlaceholder={placeholder}
        disabled={disabled}
        showCustomBroker={showCustomBroker}
        customBroker={customBroker}
      />
    </div>
  );
};